/**
 * Sprint 12 menu follow-up on the existing Role Modification ticket.
 *
 * The milestone graph is unchanged. This module only interprets the ticket
 * state so QA / FUT, CAB, and production stay pending after a development
 * menu apply, and so a human rejection does not look like a technical failure.
 *
 * Expected workflow_state (absent fields are tolerated):
 *
 * menu_change.lifecycle
 *   PENDING_APPROVAL | APPROVED | APPLIED | FAILED | REJECTED | PARTIAL_SUCCESS | RECONCILIATION_REQUIRED
 * menu_change.rejection
 *   { by, at, reason } — present when a person rejected, even if workflow_stage is still FAILED
 * menu_change.execution
 *   { status, transport, sap_changed, simulated }
 * menu_change.proposal.simulated | proposal.support
 *   simulated: true, or support: "proposal_only" | "unsupported"
 * steps[].waiver
 *   { actor, rationale, at } on qa_fut, cab, or prod_confirmation
 *
 * Canonical master fields are top-level under workflow_state:
 * task_validation: {
 *     status: "pending" | "submitted",
 *     outcome: null | "NO_AUTH_CHANGE_REQUIRED" | "AUTH_REVIEW_REQUIRED",
 *     note, actor, at,
 *     evidence: [{ filename, content_type, note, actor, at, kind }]
 * },
 * authorization_review: {
 *     status: "not_required" | "pending" | "pending_approval" | "approved" | "rejected",
 *     simulated: boolean,
 *     snapshots: { before: [AuthValue], after: [AuthValue], su24_unmerged: [AuthValue], trace_suggested: [AuthValue] },
 *     sections: { A: [AuthValue], B: [AuthValue], C: [AuthValue] }
 * },
 * authorization_selection: {
 *   retain_ids: [string], addition_ids: [string], removal_ids: [string],
 *   manual_entries: [{ op, object, instance_id, field, value, why }],
 *   final_delta: [DeltaLine], binding_hash
 * },
 * authorization_approval: { status, by, at, binding_hash, stale, invalidated },
 * authorization_execution: {
 *   status, simulated, simulation_available, sap_changed, receipt, real_write_blocked, blocker
 * }
 *
 * Older post_change_review.task_validation and
 * post_change_review.authorization_review are read as compatibility fallbacks.
 *
 * AuthValue: {
 *   object, instance_id, field, value | values,
 *   origin: "already_present" | "newly_added" | "SU24_proposal_only" | "trace_suggested",
 *   activity, org_level, why, confidence: "strong" | "weak",
 *   retain, selected, eligible_removal, protected, simulated
 * }
 *
 * DeltaLine: { op: "add" | "remove" | "exclude_proposal", object, instance_id, field, value, activity, org_level, origin, why }
 *
 * Follow-up POST /workflow/:id/menu-change-follow-up
 * { action: "record_task_validation" | "submit_authorization_delta" |
 *     "approve_authorization_delta" | "reject_authorization_delta" |
 *     "execute_simulated_authorization_delta",
 *   actor, outcome, note, evidence_base64, evidence_filename, evidence_content_type,
 *   retain_ids, addition_ids, removal_ids, manual_entries, final_delta, binding_hash }
 * Menu approval stays on POST /workflow/:id/menu-change-approval and does not authorize this delta.
 */

export const NO_AUTH_CHANGE_REQUIRED = 'NO_AUTH_CHANGE_REQUIRED';
export const AUTH_REVIEW_REQUIRED = 'AUTH_REVIEW_REQUIRED';
export const GOVERNANCE_GATES = ['qa_fut', 'cab', 'prod_confirmation'];

const MENU_TYPES = new Set(['menu_change', 'fiori_catalog', 'odata_service', 'odata', 'gateway_service']);

function workflowState(ticket) {
  const state = ticket?.workflow_state;
  if (state && typeof state === 'object') return state;
  if (typeof state === 'string') {
    try {
      const parsed = JSON.parse(state);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

function text(value) {
  return value == null ? '' : String(value).trim();
}

function lower(value) {
  return text(value).toLowerCase();
}

export function isMenuChangeTicket(ticket) {
  const state = workflowState(ticket);
  const type = lower(state.workflow_type || state.workflow_agent || ticket?.workflow_agent);
  return MENU_TYPES.has(type) || Boolean(state.menu_change);
}

function stepByKey(ticket, key) {
  const steps = workflowState(ticket).steps;
  if (!Array.isArray(steps)) return {};
  return steps.find((step) => step?.key === key) || {};
}

export function explicitWaiver(step, record) {
  const candidates = [step?.waiver, record?.waiver];
  if (lower(record?.status) === 'waived') candidates.push(record);
  if (lower(step?.status) === 'waived') candidates.push(step);
  for (const item of candidates) {
    if (!item || typeof item !== 'object') continue;
    const actor = text(item.actor || item.by);
    const rationale = text(item.rationale || item.reason);
    if (actor && rationale) return { actor, rationale, at: text(item.at) };
  }
  return null;
}

export function menuDecisionKind(ticket) {
  if (!isMenuChangeTicket(ticket)) return '';
  const state = workflowState(ticket);
  const change = state.menu_change && typeof state.menu_change === 'object' ? state.menu_change : {};
  const approvalStatus = lower(state.approval?.status);
  const stage = text(ticket?.workflow_stage).toUpperCase();
  const lifecycle = text(change.lifecycle).toUpperCase();
  if (change.rejection || lifecycle === 'REJECTED' || approvalStatus === 'rejected' || stage === 'REJECTED') {
    return 'rejected';
  }
  if (stage === 'FAILED' || lifecycle === 'FAILED' || lifecycle === 'RECONCILIATION_REQUIRED') {
    return 'failed';
  }
  return '';
}

export function devMenuExecuted(ticket) {
  if (!isMenuChangeTicket(ticket) || menuDecisionKind(ticket) === 'rejected') return false;
  const state = workflowState(ticket);
  const steps = Array.isArray(state.steps) ? state.steps : [];
  const statusOf = (key) => lower(steps.find((step) => step?.key === key)?.status);
  if (statusOf('apply_delta') !== 'done' || statusOf('verify_change') !== 'done') return false;
  if (steps.some((step) => lower(step?.status) === 'failed')) return false;
  const lifecycle = text(state.menu_change?.lifecycle || ticket?.workflow_stage).toUpperCase();
  return !['FAILED', 'RECONCILIATION_REQUIRED', 'PARTIAL_SUCCESS'].includes(lifecycle);
}

function gateRecord(ticket, key) {
  const value = workflowState(ticket)[key];
  return value && typeof value === 'object' ? value : {};
}

function gateRawStatus(step, record) {
  const recordStatus = lower(record?.status);
  if (['done', 'passed', 'pass', 'approved', 'confirmed'].includes(recordStatus)) return 'done';
  if (recordStatus === 'failed' || recordStatus === 'fail') return 'failed';
  if (recordStatus === 'rejected' || recordStatus === 'waived' || recordStatus === 'active' || recordStatus === 'pending') {
    return recordStatus;
  }
  return lower(step?.status);
}

function gateSettled(ticket, key) {
  const step = stepByKey(ticket, key);
  const record = gateRecord(ticket, key);
  const raw = gateRawStatus(step, record);
  return Boolean(explicitWaiver(step, record)) || raw === 'done' || raw === 'waived';
}

export function menuProgressNote(ticket) {
  const kind = menuDecisionKind(ticket);
  if (kind === 'rejected') return 'rejected';
  if (kind === 'failed') return 'technical failure';
  if (devMenuExecuted(ticket) && GOVERNANCE_GATES.some((key) => !gateSettled(ticket, key))) {
    return 'pending-gates';
  }
  return '';
}

const CATALOG_STAGE_PROGRESS = {
  AWAITING_TRANSPORT_RELEASE_APPROVAL: 'awaiting a recorded transport-release decision. Recording that decision does not release the transport in SAP.',
  AWAITING_QA_CONFIRMATION: 'awaiting QA confirmation evidence. A recorded QS4 check is not an SAP import.',
  AWAITING_FUT: 'awaiting functional user testing evidence.',
  FUT_REMEDIATION_REQUIRED: 'functional user testing did not pass. Remediation is required before CAB.',
  QA_FUT_FAILED: 'functional user testing did not pass. Remediation is required before CAB.',
  AWAITING_CAB: 'awaiting the CAB decision.',
  CAB_REJECTED: 'CAB rejected the change. Production confirmation does not proceed.',
  AWAITING_PROD_CONFIRMATION: 'awaiting production confirmation evidence. A recorded PS4 check is not an SAP import.',
};

function catalogTransportId(state) {
  const followUp = state.catalog_role_follow_up && typeof state.catalog_role_follow_up === 'object'
    ? state.catalog_role_follow_up
    : {};
  return text(
    followUp.handoff?.transport_id
    || followUp.transport_id
    || followUp.transport_verification?.transport_id,
  );
}

function catalogTransportReleasedInSap(state) {
  const release = state.transport_release_approval && typeof state.transport_release_approval === 'object'
    ? state.transport_release_approval
    : {};
  const followUp = state.catalog_role_follow_up && typeof state.catalog_role_follow_up === 'object'
    ? state.catalog_role_follow_up
    : {};
  return release.release_executed === true
    || followUp.handoff?.transport_released === true
    || followUp.transport_verification?.released === true;
}

/**
 * Progress sentence for a catalog ticket, taken from the persisted stage and
 * gate records. A reviewer confirmation is not described as an SAP release
 * or import unless that release was actually verified.
 */
export function workflowProgressDetail(ticket) {
  const state = workflowState(ticket);
  const lifecycle = state.catalog_lifecycle;
  if (!Array.isArray(lifecycle) || lifecycle.length === 0) return '';
  const stage = text(ticket?.workflow_stage).toUpperCase();
  if (stage === 'DONE') {
    const transportId = catalogTransportId(state);
    const transport = transportId ? `Transport ${transportId}` : 'The transport';
    if (catalogTransportReleasedInSap(state)) {
      return `complete. Reviewer decisions are recorded, and ${transport} release was verified in SAP.`;
    }
    return `complete. Transport-release approval, QA confirmation, FUT, CAB, and production confirmation were recorded. ${transport} was not released or imported by HALO.`;
  }
  if (CATALOG_STAGE_PROGRESS[stage]) return CATALOG_STAGE_PROGRESS[stage];
  const active = lifecycle.find((row) => ['active', 'running'].includes(lower(row?.status)));
  if (active?.label) return `awaiting ${active.label}.`;
  return '';
}

export function recordedGateWaivers(ticket) {
  return GOVERNANCE_GATES.map((key) => {
    const waiver = explicitWaiver(stepByKey(ticket, key), gateRecord(ticket, key));
    return waiver ? { key, ...waiver } : null;
  }).filter(Boolean);
}

/**
 * Rewrite collapsed Role Modification milestones for a catalog or OData ticket.
 * Sprint 11 role-modification tickets are not passed through here.
 */
export function applyMenuMilestoneTruth(ticket, milestones) {
  const rows = (Array.isArray(milestones) ? milestones : []).map((row) => ({ ...row }));
  if (menuDecisionKind(ticket) === 'rejected') {
    return rows.map((row) => (row.key === 'approval' ? { ...row, status: 'rejected' } : row));
  }
  if (!devMenuExecuted(ticket)) return rows;

  let opened = false;
  const next = rows.map((row) => {
    if (!GOVERNANCE_GATES.includes(row.key)) return row;
    const step = stepByKey(ticket, row.key);
    const record = gateRecord(ticket, row.key);
    const waiver = explicitWaiver(step, record);
    const raw = gateRawStatus(step, record);
    if (waiver || raw === 'waived') return { ...row, status: 'waived', waiver };
    if (raw === 'done') return { ...row, status: 'done' };
    if (raw === 'failed' || raw === 'rejected') {
      opened = true;
      return { ...row, status: raw };
    }
    if (!opened) {
      opened = true;
      return { ...row, status: 'active' };
    }
    return { ...row, status: 'pending' };
  });
  const gatesClosed = GOVERNANCE_GATES.every((key) => {
    const row = next.find((item) => item.key === key);
    return row?.status === 'done' || row?.status === 'waived';
  });
  return next.map((row) => {
    if (row.key !== 'complete' || gatesClosed) return row;
    return { ...row, status: 'pending' };
  });
}

function asList(value) {
  return Array.isArray(value) ? value : [];
}

function sectionRows(source, names) {
  const sections = source?.sections && typeof source.sections === 'object' ? source.sections : {};
  for (const name of names) {
    const section = sections[name];
    if (Array.isArray(section)) return section;
    if (section && typeof section === 'object') {
      const rows = section.rows || section.items || section.values;
      if (Array.isArray(rows)) return rows;
    }
    if (Array.isArray(source?.[name])) return source[name];
  }
  return [];
}

function protectedRow(row) {
  if (row?.protected === true || row?.sensitive === true || row?.manual_review === true) return true;
  const scope = lower(row?.scope || row?.sensitivity);
  if (['sensitive', 'wildcard', 'administration', 'admin', 'development'].includes(scope)) return true;
  return text(row?.value).includes('*') || asList(row?.values).some((value) => text(value).includes('*'));
}

function explodeValues(row) {
  const values = Array.isArray(row?.values)
    ? row.values
    : Array.isArray(row?.value)
      ? row.value
      : [row?.value];
  const concrete = values.filter((value) => text(value));
  if (!concrete.length) return [{ ...row, value: text(row?.value) }];
  return concrete.map((value) => ({ ...row, value: text(value), values: undefined }));
}

function valueIdentity(row) {
  return [text(row.object), text(row.instance_id || row.instance), text(row.field || row.field_name), text(row.value)].join('|');
}

function normalizeRows(rows, group) {
  const seen = new Map();
  return asList(rows).flatMap((raw) => explodeValues(raw)).map((raw) => {
    const origin = text(raw.origin || raw.source);
    const base = [text(raw.object), text(raw.instance_id || raw.instance), text(raw.field || raw.field_name), text(raw.value), origin || group].join('|');
    const count = seen.get(base) || 0;
    seen.set(base, count + 1);
    const protectedValue = protectedRow(raw);
    const eligible = group !== 'removals'
      ? false
      : !protectedValue
        && origin !== 'already_present'
        && (!origin || origin === 'newly_added')
        && raw.eligible_removal !== false
        && (origin === 'newly_added' || raw.eligible_removal === true);
    const confidence = lower(raw.confidence || raw.evidence_strength);
    let checkedByDefault = false;
    if (!protectedValue && group === 'introduced') checkedByDefault = raw.retain !== false;
    if (!protectedValue && group === 'additions') checkedByDefault = confidence === 'strong' || confidence === 'high' || raw.selected === true;
    if (eligible && group === 'removals') checkedByDefault = raw.selected === true;
    let decisionHint = '';
    if (protectedValue) decisionHint = 'Stops for manual Security review';
    else if (group === 'introduced' && origin === 'SU24_proposal_only') decisionHint = 'Uncheck to exclude this yet-unmerged proposal';
    else if (group === 'introduced') decisionHint = 'Uncheck to remove this newly added value';
    else if (group === 'removals' && !eligible) decisionHint = 'Not eligible — pre-existing permission';
    else if (group === 'removals') decisionHint = 'Eligible because this change introduced it. Unchecked until you select it.';
    const suppliedId = text(
      raw.id || raw.checkbox_id || raw.selection_id || raw.delta_id || raw.value_id,
    );
    return {
      id: suppliedId || (count ? `${base}#${count}` : base),
      object: text(raw.object) || '—',
      instance_id: text(raw.instance_id || raw.instance),
      field: text(raw.field || raw.field_name) || '—',
      value: text(raw.value),
      activity: text(raw.activity),
      org_level: text(raw.org_level || raw.org),
      origin,
      why: text(raw.why || raw.reason || raw.explanation),
      confidence,
      protected: protectedValue,
      eligible,
      simulated: raw.simulated === true || lower(raw.source) === 'simulated',
      interactive: group === 'removals' ? eligible : !protectedValue,
      checkedByDefault,
      decisionHint,
    };
  });
}

function deriveIntroduced(auth) {
  const snapshots = auth?.snapshots && typeof auth.snapshots === 'object' ? auth.snapshots : {};
  const before = new Set(asList(snapshots.before).flatMap((row) => explodeValues(row)).map(valueIdentity));
  const after = asList(snapshots.after).flatMap((row) => explodeValues({ ...row, origin: row.origin || 'newly_added' }))
    .filter((row) => !before.has(valueIdentity(row)));
  const afterKeys = new Set(after.map(valueIdentity));
  const proposals = asList(snapshots.su24_unmerged || snapshots.su24_proposal_only)
    .flatMap((row) => explodeValues({ ...row, origin: 'SU24_proposal_only' }))
    .filter((row) => !afterKeys.has(valueIdentity(row)));
  return [...after, ...proposals];
}

export function groupAuthorizationRows(rows) {
  const objects = [];
  const byObject = new Map();
  asList(rows).forEach((row) => {
    let objectGroup = byObject.get(row.object);
    if (!objectGroup) {
      objectGroup = { object: row.object, instances: [], byInstance: new Map() };
      byObject.set(row.object, objectGroup);
      objects.push(objectGroup);
    }
    let instance = objectGroup.byInstance.get(row.instance_id);
    if (!instance) {
      instance = { instance_id: row.instance_id, fields: [], byField: new Map() };
      objectGroup.byInstance.set(row.instance_id, instance);
      objectGroup.instances.push(instance);
    }
    let field = instance.byField.get(row.field);
    if (!field) {
      field = { field: row.field, values: [] };
      instance.byField.set(row.field, field);
      instance.fields.push(field);
    }
    field.values.push(row);
  });
  return objects.map(({ object, instances }) => ({
    object,
    instances: instances.map(({ instance_id, fields }) => ({ instance_id, fields })),
  }));
}

function normalizeManual(rows) {
  return asList(rows).map((row) => ({
    op: lower(row?.op) === 'remove' || row?.op === '-' ? 'remove' : 'add',
    object: text(row?.object),
    instance_id: text(row?.instance_id || row?.instance),
    field: text(row?.field),
    value: text(row?.value),
    why: text(row?.why || row?.reason),
  })).filter((row) => row.object && row.field && row.value && row.why);
}

function compatibilitySelection(auth) {
  return auth?.selection && typeof auth.selection === 'object' ? auth.selection : {};
}

function selectionMap(rows, ids, compatMap) {
  if (Array.isArray(ids)) {
    const selected = new Set(ids.map(text));
    return Object.fromEntries(rows.map((row) => [row.id, selected.has(row.id)]));
  }
  return compatMap && typeof compatMap === 'object' ? { ...compatMap } : null;
}

function normalizePersistedSelection(source, auth, introduced, additions, removals) {
  const compat = compatibilitySelection(auth);
  const hasMasterSelection = source && typeof source === 'object' && (
    Array.isArray(source.retain_ids)
    || Array.isArray(source.addition_ids)
    || Array.isArray(source.removal_ids)
  );
  if (!hasMasterSelection && !Object.keys(compat).length) return null;
  return {
    introduced: selectionMap(introduced, source?.retain_ids, compat.introduced) || {},
    additions: selectionMap(additions, source?.addition_ids, compat.additions) || {},
    removals: selectionMap(removals, source?.removal_ids, compat.removals) || {},
  };
}

export function readPostChangeReview(ticket) {
  const state = workflowState(ticket);
  const raw = state.post_change_review && typeof state.post_change_review === 'object' ? state.post_change_review : {};
  const task = state.task_validation && typeof state.task_validation === 'object'
    ? state.task_validation
    : raw.task_validation && typeof raw.task_validation === 'object'
      ? raw.task_validation
      : {};
  const outcome = text(task.outcome || raw.outcome);
  const auth = state.authorization_review && typeof state.authorization_review === 'object'
    ? state.authorization_review
    : raw.authorization_review && typeof raw.authorization_review === 'object'
      ? raw.authorization_review
      : {};
  const selectionSource = state.authorization_selection && typeof state.authorization_selection === 'object'
    ? state.authorization_selection
    : raw.authorization_selection && typeof raw.authorization_selection === 'object'
      ? raw.authorization_selection
      : {};
  const approval = state.authorization_approval && typeof state.authorization_approval === 'object'
    ? state.authorization_approval
    : raw.authorization_approval && typeof raw.authorization_approval === 'object'
      ? raw.authorization_approval
      : auth.approval && typeof auth.approval === 'object'
        ? auth.approval
        : {};
  const execution = state.authorization_execution && typeof state.authorization_execution === 'object'
    ? state.authorization_execution
    : raw.authorization_execution && typeof raw.authorization_execution === 'object'
      ? raw.authorization_execution
      : auth.execution && typeof auth.execution === 'object'
        ? auth.execution
        : {};
  let introduced = normalizeRows(
    sectionRows(auth, ['A', 'a', 'introduced', 'newly_introduced', 'section_a']),
    'introduced',
  );
  if (!introduced.length) introduced = normalizeRows(deriveIntroduced(auth), 'introduced');
  const additions = normalizeRows(
    sectionRows(auth, ['B', 'b', 'additions', 'recommended_additions', 'section_b']).length
      ? sectionRows(auth, ['B', 'b', 'additions', 'recommended_additions', 'section_b'])
      : asList(auth.snapshots?.trace_suggested),
    'additions',
  );
  const removals = normalizeRows(
    sectionRows(auth, ['C', 'c', 'removals', 'suggested_removals', 'section_c']),
    'removals',
  );
  const manual = normalizeManual(
    selectionSource.manual_entries || selectionSource.manual || auth.manual,
  );
  const selection = normalizePersistedSelection(
    selectionSource,
    auth,
    introduced,
    additions,
    removals,
  );
  const hasReview = Boolean(
    outcome === AUTH_REVIEW_REQUIRED
    || introduced.length
    || additions.length
    || removals.length
    || manual.length
    || text(auth.status)
    || text(approval.status),
  );
  let phase = 'hidden';
  if (isMenuChangeTicket(ticket)) {
    if (outcome === NO_AUTH_CHANGE_REQUIRED) phase = 'no_auth_change';
    else if (hasReview) phase = 'auth_review';
    else if (devMenuExecuted(ticket)) phase = 'task_validation';
  }
  return {
    phase,
    outcome,
    task,
    introduced,
    additions,
    removals,
    manual,
    selection,
    selectionSource,
    approval,
    execution,
    bindingHash: text(
      approval.binding_hash
      || approval.approval_hash
      || approval.delta_hash
      || approval.selection_hash
      || selectionSource.binding_hash
      || selectionSource.delta_hash
      || auth.binding_hash,
    ),
    approvalStale:
      approval.stale === true
      || approval.invalidated === true
      || ['stale', 'invalidated'].includes(lower(approval.status)),
    simulated:
      auth.simulated === true
      || execution.simulated === true
      || asList(auth.snapshots?.su24_unmerged).some((row) => row?.simulated === true),
    simulationAvailable:
      auth.simulation_available === true
      || auth.simulation_advertised === true
      || approval.simulation_available === true
      || execution.simulation_available === true
      || execution.simulation_advertised === true
      || execution.simulation?.available === true
      || auth.capabilities?.simulated_execution === true
      || state.capabilities?.execute_simulated_authorization_delta === true
      || state.authorization_simulation_available === true,
    storedDelta: asList(selectionSource.final_delta || auth.final_delta),
    waivers: recordedGateWaivers(ticket),
  };
}

function checkedValue(selection, group, row) {
  const chosen = selection?.[group];
  if (chosen && Object.prototype.hasOwnProperty.call(chosen, row.id)) return Boolean(chosen[row.id]);
  return row.checkedByDefault;
}

function deltaLine(row, op, why) {
  return {
    op,
    object: row.object,
    instance_id: row.instance_id,
    field: row.field,
    value: row.value,
    activity: row.activity || '',
    org_level: row.org_level || '',
    origin: row.origin || (op === 'add' || op === 'remove' ? 'manual' : ''),
    why: why || row.why || '',
  };
}

export function buildFinalAuthorizationDelta({ introduced = [], additions = [], removals = [], manual = [], selection = {} }) {
  const lines = [];
  introduced.forEach((row) => {
    if (row.protected) return;
    const retain = checkedValue(selection, 'introduced', row);
    if (row.origin === 'SU24_proposal_only') {
      lines.push(deltaLine(
        row,
        retain ? 'add' : 'exclude_proposal',
        retain ? (row.why || 'Retain unmerged proposal') : 'Exclude from yet-unmerged proposal',
      ));
      return;
    }
    if (!retain) lines.push(deltaLine(row, 'remove', row.why || 'Remove existing newly-added value'));
  });
  additions.forEach((row) => {
    if (row.protected || !checkedValue(selection, 'additions', row)) return;
    lines.push(deltaLine(row, 'add', row.why));
  });
  removals.forEach((row) => {
    if (!row.eligible || row.protected || !checkedValue(selection, 'removals', row)) return;
    lines.push(deltaLine(row, 'remove', row.why));
  });
  normalizeManual(manual).forEach((row) => {
    lines.push(deltaLine(row, row.op, row.why));
  });
  return lines.sort((a, b) => (
    [a.object, a.instance_id, a.field, a.value, a.op].join('|')
      .localeCompare([b.object, b.instance_id, b.field, b.value, b.op].join('|'))
  ));
}

export function authorizationDeltaKey(delta) {
  return JSON.stringify(delta);
}

export function seedAuthorizationSelection(review) {
  if (review?.selection) {
    return {
      introduced: { ...(review.selection.introduced || {}) },
      additions: { ...(review.selection.additions || {}) },
      removals: { ...(review.selection.removals || {}) },
    };
  }
  const selection = { introduced: {}, additions: {}, removals: {} };
  ['introduced', 'additions', 'removals'].forEach((group) => {
    asList(review?.[group]).forEach((row) => {
      selection[group][row.id] = row.checkedByDefault;
    });
  });
  return selection;
}

/** Convert UI checkbox maps to the master endpoint's selected-ID contract. */
export function buildAuthorizationSelectionPayload(review, selection, manual, finalDelta) {
  const selectedIds = (group) => asList(review?.[group])
    .filter((row) => (
      Boolean(selection?.[group]?.[row.id])
      && (group !== 'removals' || row.eligible)
    ))
    .map((row) => row.id);
  return {
    retain_ids: selectedIds('introduced'),
    addition_ids: selectedIds('additions'),
    removal_ids: selectedIds('removals'),
    manual_entries: normalizeManual(manual),
    final_delta: asList(finalDelta),
  };
}

/** Request body for the single combined menu-change-follow-up endpoint. */
export function buildMenuFollowUpRequest(payload = {}, { actor, evidenceBase64 } = {}) {
  const selected = payload.authorizationSelection || {};
  return {
    action: payload.action,
    actor,
    outcome: payload.outcome,
    note: payload.note || '',
    evidence_base64: evidenceBase64,
    evidence_filename: payload.evidenceFile?.name,
    evidence_content_type: payload.evidenceFile?.type,
    retain_ids: selected.retain_ids,
    addition_ids: selected.addition_ids,
    removal_ids: selected.removal_ids,
    manual_entries: selected.manual_entries,
    final_delta: selected.final_delta,
    binding_hash: payload.bindingHash || undefined,
    confirmation: payload.confirmation,
    profile_confirmation: payload.profile_confirmation,
    proposal_hash: payload.proposal_hash,
    selected_ids: payload.selected_ids,
    org_values: payload.org_values,
    open_fields: payload.open_fields,
    justification: payload.justification,
    phase: payload.phase,
    test_user: payload.test_user,
    context: payload.context,
    trace_checks: payload.trace_checks,
    transport_objects: payload.transport_objects,
  };
}

export function authorizationApprovalLabel(status) {
  const value = lower(status);
  if (value === 'approved') return 'Approved';
  if (value === 'rejected') return 'Rejected';
  if (value === 'stale' || value === 'invalidated') return 'Invalidated — approval required again';
  if (['pending', 'pending_approval', 'awaiting_approval', 'submitted'].includes(value)) {
    return 'Pending separate approval';
  }
  return 'Not requested';
}
